<template>
  <div class="dock" :class="['dock-' + shared.position, { 'dock-dragging': dragging }]">
    <button
      type="button"
      class="dock-handle"
      aria-label="Move toolbar"
      title="Drag to move toolbar"
      @pointerdown="startDrag"
    >
      <span class="dock-handle-grip"></span>
    </button>
    <div class="dock-items">
      <slot></slot>
    </div>
  </div>
</template>

<script>
const STORAGE_KEY = 'emdr_dock_position';

function readStoredPosition() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'left' || stored === 'right' || stored === 'bottom') {
      return stored;
    }
  } catch (e) {
    // localStorage unavailable (private mode, etc.) - ignore
  }
  return 'bottom';
}

export default {
  name: 'Dock',
  data() {
    return {
      shared: {
        position: readStoredPosition(),
        activeId: null
      },
      dragging: false
    };
  },
  provide() {
    return { dockShared: this.shared };
  },
  beforeDestroy() {
    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerup', this.handlePointerUp);
  },
  methods: {
    startDrag(e) {
      e.preventDefault();
      this.dragging = true;
      this.shared.activeId = null;
      window.addEventListener('pointermove', this.handlePointerMove);
      window.addEventListener('pointerup', this.handlePointerUp);
    },
    handlePointerMove(e) {
      const x = e.clientX;
      const y = e.clientY;
      const distances = {
        left: x,
        right: window.innerWidth - x,
        bottom: window.innerHeight - y
      };
      const next = Object.keys(distances).reduce((a, b) => (distances[a] <= distances[b] ? a : b));
      if (this.shared.position !== next) {
        this.shared.position = next;
      }
    },
    handlePointerUp() {
      this.dragging = false;
      window.removeEventListener('pointermove', this.handlePointerMove);
      window.removeEventListener('pointerup', this.handlePointerUp);
      try {
        localStorage.setItem(STORAGE_KEY, this.shared.position);
      } catch (e) {
        // ignore storage errors
      }
    }
  }
};
</script>

<style scoped>
.dock {
  position: fixed;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(30, 40, 38, 0.72);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 20px;
  padding: 0.5rem;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  z-index: 40;
  transition: left 0.25s ease, right 0.25s ease, bottom 0.25s ease, top 0.25s ease, transform 0.25s ease;
}
.dock-dragging {
  transition: none;
}

.dock-bottom {
  flex-direction: row;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
}
.dock-left {
  flex-direction: column;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
}
.dock-right {
  flex-direction: column;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
}

.dock-items {
  display: flex;
  gap: 0.35rem;
}
.dock-bottom .dock-items {
  flex-direction: row;
  align-items: center;
}
.dock-left .dock-items,
.dock-right .dock-items {
  flex-direction: column;
  align-items: center;
}

.dock-handle {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  cursor: grab;
  touch-action: none;
}
.dock-handle:hover {
  background: rgba(255, 255, 255, 0.16);
}
.dock-handle:active {
  cursor: grabbing;
}
.dock-bottom .dock-handle {
  margin-right: 0.15rem;
}
.dock-left .dock-handle,
.dock-right .dock-handle {
  margin-bottom: 0.15rem;
}

.dock-handle-grip {
  width: 14px;
  height: 14px;
  background-image: radial-gradient(rgba(255, 255, 255, 0.65) 1.4px, transparent 1.4px);
  background-size: 5px 5px;
  background-position: 0 0;
}

@media (max-width: 600px) {
  .dock-left,
  .dock-right {
    left: auto;
    right: auto;
  }
  .dock-left {
    left: 8px;
  }
  .dock-right {
    right: 8px;
  }
}
</style>
